<template>
  <div class="peak-panel">
    <v-card variant="outlined" rounded="lg" class="group-card pa-4 mb-6">
      <div class="d-flex flex-wrap align-center ga-6">
        <div class="d-flex align-center ga-2">
          <span class="text-body-2 font-weight-medium">自动需量</span>
          <v-tooltip location="top" text="按站点负荷自动调整需量控制目标">
            <template #activator="{ props: tprops }">
              <v-icon v-bind="tprops" size="18" color="medium-emphasis">mdi-help-circle-outline</v-icon>
            </template>
          </v-tooltip>
          <v-switch v-model="autoDemand" hide-details color="primary" density="compact" inset />
        </div>
        <div class="flex-grow-1 min-width-0">
          <div class="d-flex align-center ga-2 mb-2">
            <span class="text-body-2 font-weight-medium">自定义控制权限</span>
            <v-tooltip location="top" text="勾选后可在策略引擎中启用对应能力">
              <template #activator="{ props: tprops }">
                <v-icon v-bind="tprops" size="18" color="medium-emphasis">mdi-help-circle-outline</v-icon>
              </template>
            </v-tooltip>
          </div>
          <div class="d-flex flex-wrap ga-4">
            <v-checkbox v-model="permAntiReflux" hide-details density="compact" label="防逆流" color="primary" />
            <v-checkbox v-model="permTfRatio" hide-details density="compact" label="箱变容量配比" color="primary" />
            <v-checkbox v-model="permReactive" hide-details density="compact" label="无功工作模式" color="primary" />
          </div>
        </div>
      </div>
    </v-card>

    <div class="text-subtitle-2 font-weight-semibold mb-3">联合调度分组</div>
    <v-row dense class="mb-8">
      <v-col v-for="g in groups" :key="g.id" cols="12" md="6">
        <v-card variant="outlined" rounded="lg" class="group-card pa-4">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-3">
            <div class="d-flex align-center ga-2">
              <v-avatar size="28" color="primary" class="text-caption font-weight-bold">{{ g.id }}</v-avatar>
              <span class="text-body-2">整组需量: <strong>{{ g.demandKw }}</strong> kW</span>
            </div>
            <v-btn variant="text" color="primary" size="small" class="text-none" @click="openDemandDialog(g)">
              修改
            </v-btn>
          </div>
          <div class="d-flex flex-wrap ga-2">
            <v-chip
              v-for="el in g.elinks"
              :key="el.id"
              :variant="el.active ? 'flat' : 'outlined'"
              :color="el.active ? 'primary' : undefined"
              size="small"
              class="elink-chip"
              tabindex="0"
              role="button"
              :aria-pressed="el.active"
              :aria-label="`${el.label}${el.active ? '（已选中）' : ''}`"
              @click="selectElink(g, el)"
              @keydown.enter.prevent="selectElink(g, el)"
              @keydown.space.prevent="selectElink(g, el)"
            >
              <template #prepend>
                <span
                  class="elink-dot"
                  :class="
                    el.active ? 'elink-dot--active' : el.online ? 'elink-dot--on' : 'elink-dot--off'
                  "
                />
              </template>
              {{ el.label }}
            </v-chip>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card variant="outlined" rounded="lg" class="schedule-card pa-4 pa-md-5">
      <div class="d-flex align-center flex-wrap ga-2 mb-5 template-toolbar">
        <div
          v-for="(t, idx) in templates"
          :key="t"
          class="template-pill"
          :class="{ 'template-pill--active': activeTemplate === t }"
        >
          <button type="button" class="template-pill__select" @click="activeTemplate = t">
            {{ t }}
          </button>
          <v-tooltip location="top" text="删除此模板">
            <template #activator="{ props: delProps }">
              <button
                v-bind="delProps"
                type="button"
                class="template-pill__close"
                :disabled="templates.length <= 1"
                aria-label="删除模板"
                @click.stop="removeTemplate(t, idx)"
              >
                <v-icon size="14">mdi-close</v-icon>
              </button>
            </template>
          </v-tooltip>
        </div>
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-plus"
          rounded="lg"
          class="text-none template-add-btn"
          @click="addTemplate"
        >
          新增
        </v-btn>
      </div>

      <div class="field-label text-caption text-medium-emphasis mb-2">生效月份</div>
      <div class="d-flex flex-wrap ga-2 mb-5 month-row">
        <v-btn
          v-for="m in 12"
          :key="m"
          size="small"
          :variant="currentSchedule.monthSet.has(m) ? 'flat' : 'outlined'"
          :color="currentSchedule.monthSet.has(m) ? 'primary' : undefined"
          rounded="lg"
          class="month-btn"
          @click="toggleMonth(m)"
        >
          {{ m }}
        </v-btn>
      </div>

      <div class="mb-5">
        <div class="d-flex align-center flex-wrap ga-4">
          <div class="d-flex align-center ga-2">
            <span class="text-body-2 text-medium-emphasis">生效日期</span>
            <v-select
              v-model="currentSchedule.effectiveDay"
              :items="dayOptions"
              density="compact"
              variant="outlined"
              hide-details
              class="effective-day-select"
              rounded="lg"
            />
          </div>
        </div>
        <div v-if="currentSchedule.effectiveDay === '自定义'" class="d-flex flex-wrap ga-2 mt-3 weekday-row">
          <v-btn
            v-for="wd in weekdayItems"
            :key="wd.value"
            size="small"
            :variant="currentSchedule.customWeekdaySet.has(wd.value) ? 'flat' : 'outlined'"
            :color="currentSchedule.customWeekdaySet.has(wd.value) ? 'primary' : undefined"
            rounded="lg"
            class="weekday-btn"
            @click="toggleWeekday(wd.value)"
          >
            {{ wd.label }}
          </v-btn>
        </div>
      </div>

      <div class="timeline-wrap rounded-xl pa-4">
        <div class="timeline-grid">
          <div class="timeline-legend-row d-flex align-center justify-end flex-wrap ga-4 text-caption mb-1">
            <span class="d-flex align-center ga-2 legend-item">
              <i class="legend-sq legend-sq--d" />放电
            </span>
            <span class="d-flex align-center ga-2 legend-item">
              <i class="legend-sq legend-sq--c" />充电
            </span>
          </div>
          <span class="timeline-y-label text-caption text-medium-emphasis">放电</span>
          <div class="timeline-lane timeline-lane--d">
            <div
              v-for="seg in dischargeSegments"
              :key="seg.id"
              class="timeline-seg timeline-seg--d"
              :style="blockStyle(seg)"
            >
              <span class="timeline-seg__lbl text-caption">{{ seg.label }}</span>
            </div>
          </div>
          <span class="timeline-y-label text-caption text-medium-emphasis">充电</span>
          <div class="timeline-lane timeline-lane--c">
            <div
              v-for="seg in chargeSegments"
              :key="seg.id"
              class="timeline-seg timeline-seg--c"
              :style="blockStyle(seg)"
            >
              <span class="timeline-seg__lbl text-caption">{{ seg.label }}</span>
            </div>
          </div>
          <span class="timeline-y-spacer" aria-hidden="true" />
          <div class="timeline-axis text-caption text-medium-emphasis">
            <span v-for="h in axisHours" :key="h" class="axis-tick">{{ h }}</span>
          </div>
        </div>
      </div>
    </v-card>

    <v-dialog v-model="demandDialogOpen" max-width="440" scroll-strategy="close">
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-semibold">修改整组需量</v-card-title>
        <v-card-subtitle v-if="demandEditGroup" class="pt-1 pb-0">
          分组 {{ demandEditGroup.id }}
        </v-card-subtitle>
        <v-card-text class="pt-4">
          <v-text-field
            v-model.number="demandInputKw"
            label="整组需量"
            type="number"
            suffix="kW"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            :min="0"
            :max="999999"
            autofocus
            @keyup.enter="saveDemandDialog"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="closeDemandDialog">取消</v-btn>
          <v-btn color="primary" variant="flat" rounded="lg" class="text-none" @click="saveDemandDialog">
            确定
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'

const autoDemand = ref(false)
const permAntiReflux = ref(true)
const permTfRatio = ref(false)
const permReactive = ref(false)

const groups = reactive([
  {
    id: 1,
    demandKw: 1830,
    elinks: [
      { id: '1', label: '1#eLink', active: true, online: true },
      { id: '2', label: '2#eLink', active: false, online: true },
      { id: '3', label: '3#eLink', active: false, online: true },
    ],
  },
  {
    id: 2,
    demandKw: 1835,
    elinks: [
      { id: '4', label: '4#eLink', active: false, online: true },
      { id: '5', label: '5#eLink', active: false, online: false },
      { id: '6', label: '6#eLink', active: false, online: false },
    ],
  },
])

function selectElink(group, el) {
  for (const g of groups) {
    for (const e of g.elinks) {
      e.active = g.id === group.id && e.id === el.id
    }
  }
}

const demandDialogOpen = ref(false)
const demandEditGroup = ref(null)
const demandInputKw = ref(0)

function openDemandDialog(g) {
  demandEditGroup.value = g
  demandInputKw.value = g.demandKw
  demandDialogOpen.value = true
}

function closeDemandDialog() {
  demandDialogOpen.value = false
  demandEditGroup.value = null
}

function saveDemandDialog() {
  const g = demandEditGroup.value
  if (!g) return
  let v = Number(demandInputKw.value)
  if (Number.isNaN(v) || v < 0) v = 0
  if (v > 999999) v = 999999
  g.demandKw = Math.round(v)
  closeDemandDialog()
}

watch(demandDialogOpen, (open) => {
  if (!open) demandEditGroup.value = null
})

const templates = ref(['T1', 'T2'])
const activeTemplate = ref('T1')
const dayOptions = ['每天', '工作日', '周末', '自定义']

function defaultTimelineSegments() {
  return [
    { id: 'a', kind: 'discharge', start: 1.25 / 24, end: 7.5 / 24, label: '功率: 10kW' },
    { id: 'b', kind: 'charge', start: 7.5 / 24, end: 18 / 24, label: '功率: 10kW' },
    { id: 'c', kind: 'discharge', start: 18 / 24, end: 1, label: '功率: 10kW' },
  ]
}

function createTemplateState() {
  return {
    monthSet: new Set([1, 2, 3, 4, 5, 6, 7, 8, 9]),
    effectiveDay: '每天',
    customWeekdaySet: new Set([1, 2, 3, 4]),
    timelineSegments: defaultTimelineSegments(),
  }
}

const templateStates = reactive({})

function ensureTemplateState(name) {
  if (!templateStates[name]) {
    templateStates[name] = createTemplateState()
  }
}

templates.value.forEach(ensureTemplateState)

const currentSchedule = computed(() => {
  ensureTemplateState(activeTemplate.value)
  return templateStates[activeTemplate.value]
})

const dischargeSegments = computed(() =>
  currentSchedule.value.timelineSegments.filter((s) => s.kind === 'discharge')
)
const chargeSegments = computed(() =>
  currentSchedule.value.timelineSegments.filter((s) => s.kind === 'charge')
)

/** 1=周一 … 7=周日，与「自定义」配套 */
const weekdayItems = [
  { value: 1, label: '周一' },
  { value: 2, label: '周二' },
  { value: 3, label: '周三' },
  { value: 4, label: '周四' },
  { value: 5, label: '周五' },
  { value: 6, label: '周六' },
  { value: 7, label: '周日' },
]
function toggleWeekday(d) {
  const cs = currentSchedule.value
  const next = new Set(cs.customWeekdaySet)
  if (next.has(d)) next.delete(d)
  else next.add(d)
  cs.customWeekdaySet = next
}

const axisHours = ['00:00', '02:00', '04:00', '06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00', '24:00']

function nextTemplateName() {
  const nums = templates.value.map((t) => {
    const m = /^T(\d+)$/.exec(t)
    return m ? Number(m[1]) : 0
  })
  return `T${Math.max(0, ...nums) + 1}`
}

function addTemplate() {
  const name = nextTemplateName()
  templates.value = [...templates.value, name]
  ensureTemplateState(name)
  activeTemplate.value = name
}

function removeTemplate(id, idx) {
  if (templates.value.length <= 1) return
  const nextList = templates.value.filter((x) => x !== id)
  delete templateStates[id]
  templates.value = nextList
  if (activeTemplate.value === id) {
    const newIdx = Math.min(idx, nextList.length - 1)
    activeTemplate.value = nextList[newIdx]
  }
}

function toggleMonth(m) {
  const cs = currentSchedule.value
  const next = new Set(cs.monthSet)
  if (next.has(m)) next.delete(m)
  else next.add(m)
  cs.monthSet = next
}

function blockStyle(seg) {
  const left = seg.start * 100
  const w = (seg.end - seg.start) * 100
  return { left: `${left}%`, width: `${w}%` }
}

function serializeTemplate(id) {
  const s = templateStates[id]
  if (!s) return { id, months: [], effectiveDay: '每天', customWeekdays: undefined, timeline: [] }
  return {
    id,
    months: [...s.monthSet].sort((a, b) => a - b),
    effectiveDay: s.effectiveDay,
    customWeekdays:
      s.effectiveDay === '自定义' ? [...s.customWeekdaySet].sort((a, b) => a - b) : undefined,
    timeline: s.timelineSegments.map((seg) => ({ ...seg })),
  }
}

defineExpose({
  getDraft: () => ({
    mode: 'peakShaving',
    autoDemand: autoDemand.value,
    permissions: {
      antiReflux: permAntiReflux.value,
      tfRatio: permTfRatio.value,
      reactive: permReactive.value,
    },
    groups,
    templates: [...templates.value],
    activeTemplate: activeTemplate.value,
    templateSchedules: templates.value.map(serializeTemplate),
  }),
})
</script>

<style scoped>
.peak-panel {
  max-width: 1100px;
}

.group-card {
  border-color: #e2e8f0 !important;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.group-card:hover {
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.07);
  border-color: #cbd5e1 !important;
}

.elink-chip {
  cursor: pointer;
}
.elink-chip:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.schedule-card {
  border-color: #e2e8f0 !important;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.field-label {
  font-weight: 600;
  letter-spacing: 0.03em;
}

.template-toolbar {
  gap: 10px !important;
}

.template-pill {
  display: inline-flex;
  align-items: stretch;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}
.template-pill:hover:not(.template-pill--active) {
  border-color: #cbd5e1;
  background: #fff;
}
.template-pill--active {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 1px rgba(var(--v-theme-primary), 0.35);
  background: #fff;
}
.template-pill__select {
  border: none;
  background: transparent;
  padding: 8px 4px 8px 16px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(var(--v-theme-on-surface), 0.78);
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}
.template-pill--active .template-pill__select {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}
.template-pill__close {
  border: none;
  background: transparent;
  padding: 0 10px 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--v-theme-on-surface), 0.45);
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease,
    opacity 0.15s ease;
}
.template-pill:hover .template-pill__close {
  color: rgba(var(--v-theme-on-surface), 0.75);
}
.template-pill--active .template-pill__close {
  background: rgba(0, 0, 0, 0.12);
  color: rgb(var(--v-theme-on-primary));
}
.template-pill--active .template-pill__close:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.18);
}
.template-pill__close:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.template-add-btn {
  font-weight: 600;
  letter-spacing: 0.01em;
}

.month-btn {
  min-width: 40px;
  transition: transform 0.15s ease;
}
.month-btn:hover {
  transform: translateY(-1px);
}

.weekday-btn {
  min-width: 52px;
}

.effective-day-select {
  max-width: 160px;
}

.legend-item {
  color: rgba(var(--v-theme-on-surface), 0.68);
  font-weight: 500;
}

.legend-sq {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  display: inline-block;
  flex-shrink: 0;
}
.legend-sq--d {
  background: #2e7d32;
  box-shadow: 0 0 0 1px rgba(46, 125, 50, 0.25);
}
.legend-sq--c {
  background: #f57c00;
  box-shadow: 0 0 0 1px rgba(245, 124, 0, 0.25);
}

.timeline-wrap {
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
}

.timeline-grid {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  column-gap: 12px;
  row-gap: 8px;
  align-items: center;
}

.timeline-legend-row {
  grid-column: 2;
}

.timeline-y-label {
  justify-self: end;
  text-align: right;
  line-height: 1.2;
  font-weight: 500;
}

.timeline-y-spacer {
  grid-column: 1;
  height: 2px;
}

.timeline-lane {
  position: relative;
  height: 40px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 10px;
  border: 1px solid #e8ecf0;
  box-shadow: inset 0 1px 1px rgba(15, 23, 42, 0.04);
}

.timeline-seg {
  position: absolute;
  top: 7px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 2px;
  box-sizing: border-box;
}
.timeline-seg--d {
  background: rgba(67, 160, 71, 0.28);
  border: 1px solid rgba(46, 125, 50, 0.65);
}
.timeline-seg--c {
  background: rgba(255, 152, 0, 0.28);
  border: 1px solid rgba(245, 124, 0, 0.75);
}
.timeline-seg__lbl {
  white-space: nowrap;
  color: rgba(15, 23, 42, 0.82);
  font-weight: 500;
  font-size: 11px;
}

.timeline-axis {
  grid-column: 2;
  display: flex;
  justify-content: space-between;
  padding-top: 4px;
  border-top: 1px solid #e2e8f0;
  margin-top: 2px;
}
.axis-tick {
  flex: 0 0 auto;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.55);
}
.axis-tick:first-child {
  transform: none;
}
.axis-tick:last-child {
  transform: translateX(0);
}

@media (prefers-reduced-motion: reduce) {
  .month-btn:hover {
    transform: none;
  }
  .template-pill,
  .template-pill__select,
  .template-pill__close,
  .group-card {
    transition: none;
  }
}

.elink-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}
.elink-dot--active {
  background: rgb(var(--v-theme-on-primary));
}
.elink-dot--on {
  background: #2e7d32;
}
.elink-dot--off {
  background: #b0bec5;
}
</style>
